import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Text,
  Button,
  Box,
  Flex,
  Icon,
  Alert,
  AlertIcon,
  Divider,
} from '@chakra-ui/react';

import { TTab } from './type';
import { CirlceIcon, allIcons, icons } from '../utils/Icon';
import EditColumns from './EditColumns';
import { updateColumn, updateTabOrder } from '../../api/tabsApi';
import { TState, useActions } from '../../redux/store';
import { useSubTabs } from '../../redux/actions';
import DragDropList from '../dragAndDrop/DragDropList';
import EditKPIs from './EditKPIs';

const Element = ({
  tab,
  idx,
  onClick,
  selected,
}: {
  tab: TTab;
  idx: number;
  onClick: any;
  selected: boolean;
}) => {
  const subTabs = useSubTabs();
  const theme = useSelector((state: TState) => state.theme);
  const isEditMode = useSelector((state: TState) => state.isEditMode);
  const { setModalType, setTabToUpdate } = useActions();

  return (
    <Box pb={3} w='100%'>
      <Flex
        p={2}
        key={tab?._id}
        textTransform='capitalize'
        onClick={() => onClick(idx)}
        borderWidth='1px'
        borderRadius={5}
        backgroundColor={selected ? theme.normal : 'white'}
        borderColor={selected ? theme.dark : 'white'}
        color={selected ? 'white' : theme.main}
        fontWeight={selected ? 'bold' : 'auto'}
        flexGrow={idx === 0 ? 1 : 0}
      >
        {tab?.icon && <Icon as={allIcons[tab?.icon]} boxSize={25} mr={2} />}
        <Text whiteSpace='nowrap'>
          {idx === 0 ? 'vue principale' : tab?.title}
        </Text>
        <Box flexGrow={1} />
        {idx !== 0 && isEditMode && (
          <>
            <CirlceIcon
              boxSize='27px'
              as={icons.edit}
              ml={3}
              onClick={() => {
                setTabToUpdate(subTabs[idx]);
                setModalType('editSubTab');
              }}
            />
            <CirlceIcon
              boxSize='27px'
              as={icons.cross}
              onClick={() => {
                setModalType('delTab');
              }}
            />
          </>
        )}
      </Flex>
    </Box>
  );
};

const ListItem = ({ item, idx }: any) => {
  const tabToUpdate = useSelector((state: TState) => state.tabToUpdate);
  const { setTabToUpdate } = useActions();

  return (
    <Element
      tab={item}
      idx={idx}
      onClick={() => setTabToUpdate(item)}
      selected={tabToUpdate?._id === item._id}
    />
  );
};

const SubTabsWithOrder = () => {
  const subTabs = useSubTabs();
  const currentTab = useSelector((state: TState) => state.currentTab);
  const tabToUpdate = useSelector((state: TState) => state.tabToUpdate);
  const tabTitles = useSelector((state: TState) => state.tabTitles);
  const theme = useSelector((state: TState) => state.theme);

  const { setModalType, getTabs, setTabToUpdate } = useActions();

  const [hasChanges, setHasChanges] = useState(false);
  const [isKPI, setIsKPI] = useState(false);
  const [advancedColumns, setAdvancedColumns] = useState(
    tabToUpdate?.tableColumns || [],
  );

  const isPlanning = currentTab?.isPlanning;
  const isDuplicate = currentTab?.isDuplicate;

  const update = async () => {
    if (!tabToUpdate) return;

    setHasChanges(false);

    await updateColumn({
      id: tabToUpdate?._id,
      data: advancedColumns?.map((ts) => ({
        ...ts,
        tableColumns: ts.tableColumns.map((t) => t.name_id),
      })),
      isPlanning,
      isDuplicate,
    });

    getTabs();
  };

  const onDrop = async (list: TTab[]) => {
    await updateTabOrder(
      list.reduce(
        (prev: string[], curr: TTab, i) =>
          i === 0 ? prev : [...prev, curr._id],
        [],
      ),
    );

    getTabs();
  };

  useEffect(() => {
    setHasChanges(false);
    tabToUpdate && setIsKPI(false);
  }, [tabToUpdate]);

  return !tabToUpdate && !isKPI ? null : (
    <Flex position='relative' flexDirection='column' h='100%'>
      <Flex alignItems='center' pb={2} gap={2}>
        <Box flexGrow={1} />
        {!isPlanning && (
          <Button
            ml={2}
            onClick={() => {
              setModalType('editFilters');
            }}
            backgroundColor={theme.light}
            color={theme.main}
            _hover={{ backgroundColor: theme.dark, color: theme.none }}
          >
            Editer les filtres
          </Button>
        )}
        <Button
          onClick={update}
          backgroundColor={theme.light}
          color={theme.main}
          _hover={{ backgroundColor: theme.dark, color: theme.none }}
        >
          Sauvegarder
        </Button>
        {hasChanges && (
          <Box flexGrow={0} ml={3}>
            <Alert status='warning' p={2} borderRadius={10}>
              <AlertIcon />
              Pensez à sauvegarder
            </Alert>
          </Box>
        )}
      </Flex>
      <Flex id='subTabsPanels' overflow='hidden' flexGrow={1}>
        <Box w={220} flexShrink={0} overflow='auto'>
          {!currentTab?.cloneId && (
            <>
              <DragDropList
                noDragIndexes={[0]}
                listItem={subTabs}
                onDrop={onDrop}
                List={ListItem}
                type='SUBTAB'
              />
              <Divider mb={3} />

              <Button
                w='100%'
                onClick={() => {
                  setModalType('addSubTab');
                }}
                backgroundColor={theme.none}
              >
                Ajouter un onglet
              </Button>
              <Divider mb={3} />
            </>
          )}
          <Button
            w='100%'
            mt={3}
            onClick={() => {
              setTabToUpdate(null);
              setIsKPI(true);
            }}
            textTransform='capitalize'
            borderWidth='1px'
            borderRadius={5}
            _hover={{}}
            backgroundColor={isKPI ? theme.normal : 'white'}
            borderColor={isKPI ? theme.dark : 'white'}
            color={isKPI ? 'white' : theme.main}
            fontWeight={isKPI ? 'bold' : 'auto'}
          >
            Indicateurs
          </Button>
        </Box>
        <Box
          flexGrow={1}
          borderRadius='5px'
          overflow='auto'
          p={3}
          ml={3}
          bgColor={theme.none}
        >
          {isKPI && <EditKPIs />}
          {currentTab?.cloneId && !isKPI ? (
            <Flex
              alignItems='center'
              bgColor={theme.light}
              w='fit-content'
              p={2}
              borderRadius={5}
            >
              Cette page est un clone de la page
              <Text
                textTransform='capitalize'
                fontSize='1.1em'
                fontWeight='500'
                ml={1}
              >
                {tabTitles[currentTab?.cloneId] || ''}
              </Text>
              , aucune modification est possible excepté les filtres et
              indicateurs
            </Flex>
          ) : (
            tabToUpdate && (
              <EditColumns
                tab={tabToUpdate as TTab}
                setAdvancedColumns={setAdvancedColumns}
                setHasChanges={setHasChanges}
              />
            )
          )}
        </Box>
      </Flex>
    </Flex>
  );
};

export default SubTabsWithOrder;
